import React, {useCallback, useEffect, useMemo} from 'react';
import {Team} from '../../interfaces';
import {Box, Button, Modal, TextField, Typography} from '@mui/material';
import {Control, Controller, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {DateTime} from 'luxon';
import styles from '../../styles';
import FormTextField from '../shared/FormTextField';
import {updateTeam} from '../../api/adminAPI';
import FormCheckbox from '../shared/FormCheckbox';
import {formatDateOnly} from '../../services/dateService';

type TeamUpdateForm = {
  id: number;
  name: string;
  code: string;
  organizationId: number;
  teamSessionGroupsEnabled: boolean;
  isPro: boolean;
  hideLeaderboard: boolean;
  membershipTrialEndDate: string;
};

interface Props {
  team: Team;
  loading: boolean;
  onUpdate: () => void;
}

const UpdateTeamModal: React.FC<Props> = ({team, loading, onUpdate}) => {
  const {t} = useTranslation();
  const [showUpdateDialog, setShowUpdateDialog] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const defaultValues = useMemo(
    () => ({
      ...team,
      hideLeaderboard: team.hideLeaderboard ?? false,
      membershipTrialEndDate: formatDateOnly(team.membershipTrialEndDate),
    }),
    [team],
  );
  const {handleSubmit, reset, control} = useForm<TeamUpdateForm>({
    defaultValues,
  });

  // defaultValues are only read on first mount; keep the form in sync after
  // the parent refetches the team following a save
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleUpdateDialogClick = useCallback(() => {
    setShowUpdateDialog(true);
  }, []);

  const handleAddDialogClose = useCallback(() => {
    reset();
    setPending(false);
    setShowUpdateDialog(false);
  }, [reset]);

  const handleUpdateSubmit = useCallback(
    async (data: TeamUpdateForm) => {
      setPending(true);
      const trimmed = data.membershipTrialEndDate?.trim();
      await updateTeam({
        id: data.id,
        name: data.name,
        code: data.code,
        organizationId: data.organizationId,
        teamSessionGroupsEnabled: data.teamSessionGroupsEnabled,
        isPro: data.isPro,
        hideLeaderboard: data.hideLeaderboard,
        membershipTrialEndDate: trimmed || null,
      });
      onUpdate();
      handleAddDialogClose();
    },
    [handleAddDialogClose, onUpdate],
  );

  return (
    <>
      <Button
        sx={{mb: 2, mr: 2}}
        onClick={handleUpdateDialogClick}
        disabled={loading}>
        {t('admin.updateTeam')}
      </Button>
      <Modal
        open={showUpdateDialog}
        onClose={handleAddDialogClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('admin.updateTeam')}
          </Typography>
          <form onSubmit={handleSubmit(handleUpdateSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="name"
              label={t('general.name') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="code"
              label={t('admin.code') as string}
            />
            <FormCheckbox
              control={control as unknown as Control<FieldValues>}
              name="teamSessionGroupsEnabled"
              label={t('admin.teamSessionGroupsEnabled') as string}
            />
            <FormCheckbox
              control={control as unknown as Control<FieldValues>}
              name="isPro"
              label={t('admin.isPro') as string}
            />
            <FormCheckbox
              control={control as unknown as Control<FieldValues>}
              name="hideLeaderboard"
              label={t('admin.hideLeaderboard') as string}
            />
            <Controller
              name="membershipTrialEndDate"
              control={control}
              rules={{
                validate: value => {
                  if (!value || !value.trim()) return true;
                  return DateTime.fromFormat(value.trim(), 'yyyy-MM-dd').isValid
                    ? true
                    : 'Date must be in yyyy-MM-dd format';
                },
              }}
              render={({field, fieldState: {error}}) => (
                <TextField
                  {...field}
                  label="Membership End Date (yyyy-MM-dd)"
                  variant="outlined"
                  sx={{mt: 2, mb: 2}}
                  fullWidth
                  placeholder="yyyy-MM-dd"
                  error={!!error}
                  helperText={error ? error.message : 'Leave empty for null'}
                />
              )}
            />
            <Button disabled={pending} type="submit">
              {t('general.submit')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(UpdateTeamModal);
